// CR051: Merchant List Component (Left Rail)
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, RefreshCw, Store, CheckCircle, AlertTriangle, XCircle, Plus } from 'lucide-react';
import type { MerchantBinding } from '@/pages/admin/FlyerCaptureConsole';
import { cn } from '@/lib/utils';

interface MerchantListProps {
  merchants: MerchantBinding[];
  selectedMerchant: MerchantBinding | null;
  onSelectMerchant: (merchant: MerchantBinding) => void;
  isLoading: boolean;
  onRefresh: () => void;
}

const getStatusConfig = (merchant: MerchantBinding) => {
  if (!merchant.enabled) {
    return { label: 'Disabled', color: 'bg-muted text-muted-foreground', icon: null };
  }
  if (!merchant.last_run_status) {
    return { label: 'Not Run', color: 'bg-secondary text-secondary-foreground', icon: null };
  }
  
  // Prioritize health status if available (PASS/WARN/FAIL from health check)
  const healthStatuses = ['PASS', 'WARN', 'FAIL'];
  if (healthStatuses.includes(merchant.last_run_status)) {
    switch (merchant.last_run_status) {
      case 'PASS':
        return { label: 'Pass', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle };
      case 'WARN':
        return { label: 'Warn', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: AlertTriangle };
      case 'FAIL':
        return { label: 'Fail', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle };
    }
  }
  
  // Fallback to run status
  switch (merchant.last_run_status) {
    case 'completed':
      return { label: 'Pass', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle };
    case 'failed':
      return { label: 'Failed', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle };
    case 'running':
      return { label: 'Running', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: Loader2 };
    default:
      return { label: 'Review', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: AlertTriangle };
  }
};

export const MerchantList = ({
  merchants,
  selectedMerchant,
  onSelectMerchant,
  isLoading,
  onRefresh
}: MerchantListProps) => {
  return (
    <div className="h-full flex flex-col border-r">
      <div className="p-4 border-b flex items-center justify-between">
        <h2 className="font-semibold text-lg">Merchants</h2>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={onRefresh} disabled={isLoading}>
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <Button variant="ghost" size="icon" disabled>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : merchants.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            No merchants configured
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {merchants.map((merchant) => {
              const status = getStatusConfig(merchant);
              const isSelected = selectedMerchant?.id === merchant.id;
              const StatusIcon = status.icon;

              return (
                <button
                  key={merchant.id}
                  onClick={() => onSelectMerchant(merchant)}
                  className={cn(
                    "w-full p-3 rounded-lg text-left transition-colors",
                    "hover:bg-accent",
                    isSelected && "bg-accent border-l-4 border-primary"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Store className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-medium truncate">{merchant.merchant_name}</span>
                    </div>
                    <Badge variant="secondary" className={cn("shrink-0 text-xs", status.color)}>
                      {StatusIcon && <StatusIcon className="h-3 w-3 mr-1" />}
                      {status.label}
                    </Badge>
                  </div>
                  
                  {merchant.last_run_at && (
                    <div className="mt-1 text-xs text-muted-foreground pl-6">
                      Last run: {new Date(merchant.last_run_at).toLocaleDateString()}
                    </div>
                  )}
                  
                  <div className="mt-1 flex items-center gap-2 pl-6">
                    <Badge variant="outline" className="text-xs">
                      {merchant.run_strategy === 'STRICT_RECIPE' ? 'Strict' : 'Fallback'}
                    </Badge>
                    {!merchant.enabled && (
                      <Badge variant="outline" className="text-xs text-muted-foreground">
                        Disabled
                      </Badge>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>

      <div className="p-4 border-t text-xs text-muted-foreground">
        {merchants.length} merchants configured
      </div>
    </div>
  );
};
